import * as React from 'react'

import { type AppUserProfile, isDeleted } from '../users/types.no-deps'
import type { SupportedLanguage } from '../../i18n/i18n.no-deps'
import type { UnitAttribute } from '../units/types.no-deps'
import type { CustomElement, ParagraphDescendant } from './types.no-deps'
import { isTextNode } from './helpers.no-deps'

type RichTextNodeProps = {
    element: ParagraphDescendant
    users: AppUserProfile[]
    unitAttributes: UnitAttribute[]
    attributes?: unknown
    isEditMode?: boolean
    language: SupportedLanguage
}

export const RichTextNode: React.FC<RichTextNodeProps> = (props) => {
    if (isTextNode(props.element)) {
        return props.element.text && <span>{props.element.text}</span>
    }
    switch (props.element.type) {
        case 'mention':
            return (
                <Node
                    element={props.element}
                    attributes={props.attributes}
                    userId={props.element.userId}
                    users={props.users}
                    language={props.language}
                ></Node>
            )
        case 'unit-attribute':
            return (
                <Node
                    element={props.element}
                    attributes={props.attributes}
                    name={props.element.name}
                    unitTypeId={props.element.unitTypeId}
                    unitAttributes={props.unitAttributes}
                    language={props.language}
                    isEditMode={props.isEditMode}
                ></Node>
            )
        case 'new-line':
            return <br />
        default:
            return <p {...(props.attributes as any)}>{props.children}</p>
    }
}

type NodeProps = {
    unitAttributes?: UnitAttribute[]
    name?: string
    unitTypeId?: number
    users?: AppUserProfile[]
    userId?: number
    element: CustomElement
    attributes: unknown
    language: SupportedLanguage
    isEditMode?: boolean
}

const Node: React.FC<NodeProps> = (props) => {
    const displayNode = (textContent: string) => (
        <span style={{ color: '#00b3ff' }} {...(props.attributes as any)}>
            {textContent}
        </span>
    )

    switch (props.element.type) {
        case 'mention':
            const user = React.useMemo(
                () => props.users.find((usr) => usr.userId === props.userId),
                [props.users, props.userId]
            )

            const unKnownUser = window.i18n('shared:reportSubject.title')

            const userName = user && !isDeleted(user) ? user.name : unKnownUser
            return displayNode(`@${userName}`)

        case 'unit-attribute':
            const unitAttribute = React.useMemo(
                () =>
                    props.unitAttributes.find(
                        ({ name, unitTypeId }) => name === props.name && unitTypeId === props.unitTypeId
                    ),
                [props.unitAttributes, props.name]
            )
            return displayNode(formatUnitAttribute(props, unitAttribute))

        case 'new-line':
            return <br />
    }
}

function formatUnitAttribute(props: Partial<NodeProps>, unitAttribute: UnitAttribute) {
    if (!unitAttribute) {
        return formatAttributeMissing(props.name)
    }
    if (props.isEditMode) {
        return `#${props.name}`
    }
    return getValueOrMissingFormat(unitAttribute, props.name)
}

function getValueOrMissingFormat(unitAttribute: UnitAttribute, attributeName: string) {
    return unitAttribute?.value || formatAttributeMissing(attributeName)
}

function formatAttributeMissing(attributeName: string) {
    return `[${attributeName}]`
}
